import React, { useState, useEffect } from 'react';
// eslint-disable-next-line
import { hasPermission, getSocietyPermissions } from '../utils/permissions';
import './CreateInvitation.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const CreateInvitation = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    emailId: '',
    role: '',
    roomNo: ''
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [canAcceptInvitations, setCanAcceptInvitations] = useState(false);
  const [canSendInvitations, setCanSendInvitations] = useState(false);

  useEffect(() => {
    fetchInvitations();
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      fetchRoles();
      fetchRooms();
    }
  }, [isModalOpen]);

  useEffect(() => {
    // Check permissions when component mounts
    const checkPermissions = () => {
      const hasAcceptPermission = hasPermission('org:accept-invitations');
      const hasSendPermission = hasPermission('org:send-invitations');
      
      console.log('Permission Check Results:', {
        hasAcceptPermission,
        hasSendPermission
      });
      
      setCanAcceptInvitations(hasAcceptPermission);
      setCanSendInvitations(hasSendPermission);
    };

    checkPermissions();
  }, []);

  const fetchInvitations = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const societyId = localStorage.getItem('societyId');

      if (!societyId) {
        throw new Error('No society selected');
      }

      const response = await fetch(
        `${API_URL}/api/v1/auth/orgs/${societyId}/invitations?access_token=${encodeURIComponent(token)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch invitations');
      }

      const data = await response.json();
      if (data.Data) {
        setInvitations(data.Data);
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
      setError('Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      const token = localStorage.getItem('accessToken');
      const societyId = localStorage.getItem('societyId');

      if (!societyId) {
        throw new Error('No society selected');
      }

      const response = await fetch(
        `${API_URL}/api/v1/auth/orgs/${societyId}/roles?access_token=${encodeURIComponent(token)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch roles');
      }

      const data = await response.json();
      setRoles(data || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setError('Failed to load roles');
    } finally {
      setLoadingRoles(false);
    }
  };

  const fetchRooms = async () => {
    try {
      setLoadingRooms(true);
      const token = localStorage.getItem('accessToken');
      const societyId = localStorage.getItem('societyId');

      if (!societyId) {
        throw new Error('No society selected');
      }

      const response = await fetch(
        `${API_URL}/api/v1/auth/rooms?access_token=${encodeURIComponent(token)}&org_id=${encodeURIComponent(societyId)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch rooms');
      }

      const data = await response.json();
      if (data.status && data.data) {
        setRooms(data.data.RoomNos || []);
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
      setError('Failed to load rooms');
    } finally {
      setLoadingRooms(false);
    }
  };

  const handleAction = async (invitationId, action) => {
    try {
      const token = localStorage.getItem('accessToken');
      const societyId = localStorage.getItem('societyId');

      const response = await fetch(
        `${API_URL}/api/v1/auth/orgs/${societyId}/invitations/${invitationId}/${action}?access_token=${encodeURIComponent(token)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to ${action} invitation`);
      }

      await fetchInvitations();
      setSuccess(`Invitation ${action}d successfully`);
      
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error) {
      console.error(`Error ${action}ing invitation:`, error);
      setError(error.message);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('accessToken');
      const societyId = localStorage.getItem('societyId');
      const profileData = JSON.parse(localStorage.getItem('profileData'));

      if (!societyId) {
        throw new Error('No society selected');
      }

      if (!profileData || !profileData.uid) {
        throw new Error('User profile not found');
      }

      const invitationData = {
        Email: formData.emailId,
        RoleIds: [formData.role], // Convert single role to array
        InviterUid: profileData.uid,
        RoomNo: formData.roomNo
      };

      const response = await fetch(
        `${API_URL}/api/v1/auth/orgs/${societyId}/lr/invitations?access_token=${encodeURIComponent(token)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(invitationData)
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create invitation');
      }

      setSuccess('Invitation sent successfully!');
      setFormData({
        emailId: '',
        role: '',
        roomNo: ''
      });
      
      // Refresh invitations list
      await fetchInvitations();
      
      // Close modal after successful submission
      setTimeout(() => {
        setIsModalOpen(false);
        setSuccess(null);
      }, 2000);
    } catch (error) {
      console.error('Error creating invitation:', error);
      setError(error.message || 'Failed to create invitation');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setError(null);
    setSuccess(null);
    setFormData({
      emailId: '',
      role: '',
      roomNo: ''
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Add a debug log in the render
  console.log('Render state:', {
    canAcceptInvitations,
    canSendInvitations
  });

  return (
    <div className="create-invitation-container">
      <div className="header-section">
        <h2>Society Invitations</h2>
        <button 
          className="create-button"
          onClick={() => setIsModalOpen(true)}
        >
          <i className="fas fa-plus"></i>
          Create Invitation
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {loading ? (
        <div className="loading-message">Loading invitations...</div>
      ) : (
        <div className="invitations-list">
          {invitations.length === 0 ? (
            <div className="no-invitations">
              No invitations found. Click "Create Invitation" to send one.
            </div>
          ) : (
            invitations.map((invitation) => (
              <div key={invitation.Id} className="invitation-card">
                <div className="invitation-info">
                  <h3>{invitation.EmailId}</h3>
                  <p className="invitation-role">
                    Role IDs: {invitation.RoleIds.join(', ')}
                  </p>
                  <p className="invitation-date">
                    Sent on: {formatDate(invitation.CreatedDate)}
                  </p>
                  <p className="invitation-date">
                    Expires on: {formatDate(invitation.ExpirationDate)}
                  </p>
                  <p className="invitation-status">
                    Status: <span className={`status-${invitation.Status.toLowerCase()}`}>
                      {invitation.Status}
                    </span>
                  </p>
                </div>
                {canAcceptInvitations && (
                  <div className="invitation-actions">
                    {invitation.Status === 'pending' && (
                      <>
                        <button
                          className="action-button accept"
                          onClick={() => handleAction(invitation.Id, 'accept')}
                        >
                          <i className="fas fa-check"></i>
                          Accept
                        </button>
                        <button
                          className="action-button reject"
                          onClick={() => handleAction(invitation.Id, 'reject')}
                        >
                          <i className="fas fa-times"></i>
                          Reject
                        </button>
                      </>
                    )}
                    <button
                      className="action-button revoke"
                      onClick={() => handleAction(invitation.Id, 'revoke')}
                    >
                      <i className="fas fa-ban"></i>
                      Revoke
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Create New Invitation</h3>
              <button 
                className="close-button"
                onClick={closeModal}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}
            
            <form onSubmit={handleSubmit} className="invitation-form">
              <div className="form-group">
                <label htmlFor="emailId">Email Address</label>
                <input
                  type="email"
                  id="emailId"
                  name="emailId"
                  value={formData.emailId}
                  onChange={handleChange}
                  required
                  placeholder="Enter email address"
                />
              </div>

              <div className="form-group">
                <label htmlFor="role">Role</label>
                {loadingRoles ? (
                  <div className="select-loading">Loading roles...</div>
                ) : (
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                    className="role-select"
                  >
                    <option value="">Select a role</option>
                    {roles.map((role) => (
                      <option key={role.Id} value={role.Id}>
                        {role.Name} - {role.Description}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="roomNo">Room Number</label>
                {loadingRooms ? (
                  <div className="select-loading">Loading rooms...</div>
                ) : (
                  <select
                    id="roomNo"
                    name="roomNo"
                    value={formData.roomNo}
                    onChange={handleChange}
                    required
                    className="role-select"
                  >
                    <option value="">Select a room</option>
                    {rooms.map((roomNo) => (
                      <option key={roomNo} value={roomNo}>
                        {roomNo}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateInvitation; 